import type { BasePolicyDto } from '../../../../../types/policy/policy.dto';
import type { P100QuoteDetailsDto } from '../../../../../types/quotes/details/p100-quote-details.dto';
import type {
  AdditionalInsureds,
  GlobalPropertyKeys,
  Prime100Coverages,
  SocialEngQuestions,
} from '../types';
import type { Prime100CoverageKeys } from './constants';

export interface EligibleCoverageValuesResponse {
  hardwareReplCostEndorsement: {
    isAvailable: boolean;
    default: number;
    limits: number[];
  };
  websiteMediaContentLiabilityEndorsement: {
    isAvailable: boolean;
    default?: number;
    limitDefault?: number;
    limits: number[];
    deductibles?: number[];
    deductibleDefault?: number;
  };
  computerFundsTransfer: {
    isAvailable: boolean;
    default: number;
  };
  retroactivePeriod: {
    default: number;
    values: number[];
  };
  deductible: {
    default: number;
    values: number[];
  };
  waitingPeriod: {
    default: number;
    values: number[];
  };
  limit: {
    default: number;
    values: number[];
  };
  businessIncomeCoverage: {
    default: number;
    values: number[];
  };
  socialEngEndorsement: {
    isAvailable: boolean;
    deductibles: number[];
    limitDefault: number;
    deductibleDefault: number;
    limits: number[];
  };
  extortionThreatsAndRansomPayment: {
    isAvailable: boolean;
    default: number;
    limits: number[];
  };
  telecomsFraudEndorsement: {
    isAvailable: boolean;
    default: number;
    limits: number[];
  };
  postBreachRemediationEndorsement: {
    isAvailable: boolean;
    default: number;
    limits: number[];
  };
  blanketAdditionalInsuredEndorsement?: {
    isAvailable: boolean;
  };
  serviceFraudAndCryptojacking?: {
    isAvailable: boolean;
    default: number;
    limits: number[];
  };
  dedicatedBreachCostsEndorsement?: {
    isAvailable: boolean;
    default: number;
    limits: number[];
  };
  fundsTransferFraudAndSocialEngineeringEndorsement?: {
    isAvailable: boolean;
    deductibles?: number[];
    limitDefault?: number;
    deductibleDefault?: number;
    limits?: number[];
  };
  fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement?: {
    isAvailable: boolean;
    deductibles?: number[];
    limitDefault?: number;
    deductibleDefault?: number;
    limits?: number[];
  };
  invoiceManipulationEndorsement?: {
    isAvailable: boolean;
    deductibles?: number[];
    limitDefault?: number;
    deductibleDefault?: number;
    limits?: number[];
  };
  raterVersion?: string;
}

export type TransformedEligibleCoverageValuesResponse = {
  [K in Prime100CoverageKeys | GlobalPropertyKeys]: {
    default?: number;
    values?: number[];
    isAvailable?: boolean;
    limits?: number[];
    limitDefault?: number;
    deductibles?: number[];
    deductibleDefault?: number;
  };
} & { raterVersion?: string };

export interface SocialEngineeringResponse {
  isAuthenticatingFundTransferRequests: boolean;
  reason: string[];
  isVerifyingBankAccounts: boolean;
  isPreventingUnauthorizedWireTransfers: boolean;
  excludedFromSE: boolean;
}

export interface EnhancedBusinessAndCbiResponse {
  backupFrequency?: string;
  businessContinuityMeasures?: string[];
}

/* Start Cart API types */
export interface QuoteCartListResponse {
  content: CartItem[];
  totalElements: number;
}

export type CartItem = {
  accountId: string;
  agencyId: string;
  id: string;
  quoteNumber: string;
  calculatorGeneratedData: CalculatorGeneratedData;
};

type CalculatorGeneratedData = {
  agencyId: string;
  accountId: string;
  policyId: string | null;
  limit: string;
  deductible: string;
  waitingPeriod: number;
  retroactivePeriod: number;
  premium: string;
  coverageExclusion: string | null;
  businessIncomeCoverage: number;
  computerFraudEndorsement: boolean;
  effectiveDate: string;
  endDate: string;
  socialEngEndorsement: boolean;
  socialEngLimit: number;
  socialEngDeductible: number;
  ransomPaymentEndorsement: boolean;
  ransomPaymentLimit: number;
  hardwareReplCostEndorsement: boolean;
  hardwareReplCostSubLimit: number;
  telecomsFraudEndorsement: boolean;
  telecomsFraudSubLimit: number;
  postBreachRemediationEndorsement: boolean;
  postBreachRemediationSubLimit: number;
  websiteMediaContentLiabilityEndorsement: boolean;
  websiteMediaContentLiabilitySubLimit: number;
  additionalInsureds: string[];
  extortionThreatsLimitDetail: string;
  pciFinesAndPenaltiesLimit: number;
  regulatorDefenceAndPenaltiesLimit: number;
  courtAttendanceCosts: number;
};
/* End Cart Api types */

export interface BoostRaterVersionPayload {
  effectiveDate: string;
  state: string;
  isRenewal: boolean;
}

export enum BoostRaterVersionResponse {
  V4 = 'V4',
  V5 = 'V5',
  V6 = 'V6',
}

export type GetPrime100CoveragesResponse = Record<
  Prime100Coverages,
  CoverageDetails
>;

export type CoverageDetails = {
  name: string;
  tooltip: string;
  errorMessage: string;
  ordinal: number;
};

export interface P100PricingPayload {
  accountId?: string;
  businessName: string;
  naicsCode: number;
  naicsDescription?: string;
  websites?: string[];
  revenue: number;
  numberOfEmployees: number;
  companyType: string;
  state: string;
  zipCode: string;
  aggregateLimit: number;
  deductible: number;
  waitingPeriod: number;
  businessIncomeCoverage: number;
  socialEngLimit?: number;
  socialEngDeductible?: number;
  ransomPaymentLimit?: number;
  postBreachRemediationSubLimit?: number;
  hardwareReplCostSubLimit?: number;
  telecomsFraudSubLimit?: number;
  excludedFromSE?: boolean;
  computerFraudEndorsement?: boolean;
  socialEngEndorsement?: boolean;
  postBreachRemediationEndorsement?: boolean;
  hardwareReplCostEndorsement?: boolean;
  telecomsFraudEndorsement?: boolean;
  questionEncryption: boolean;
  questionLeadership: boolean;
  questionCloud: boolean;
  questionTraining: boolean;
  isVerifyingBankAccounts?: boolean;
  isAuthenticatingFundTransferRequests?: boolean;
  isPreventingUnauthorizedWireTransfers?: boolean;
  websiteMediaContentLiabilityEndorsement?: boolean;
  extortionThreatsAndRansomPayment?: boolean;
  isTriaExcluded?: boolean;
  websiteMediaContentLiabilitySubLimit?: number;
  websiteMediaContentLiabilityDeductible?: number;
  effectiveDate: string;
  endDate?: string;
  claimHistory: number;
  yearEstablished: number;
  additionalInsureds?: AdditionalInsureds[];
  retroactivePeriod: number;
  additionalBrokerFee?: number;
  isRenewal?: boolean;
  policyId?: string;
  created?: string;
  fundsTransferFraudAndSocialEngineeringEndorsement?: boolean;
  fundsTransferFraudCoverageLimit?: number;
  socialEngineeringFraudCoverageLimit?: number;
  fundsTransferFraudCoverageDeductible?: number;
  socialEngineeringFraudCoverageDeductible?: number;
  fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement?: boolean;
  fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement?: boolean;
  invoiceManipulationEndorsement?: boolean;
  invoiceManipulationSubLimit?: number;
  invoiceManipulationDeductible?: number;
  additionalDefenseExpensesLimitEndorsement?: boolean;
  additionalDefenseExpensesLimit?: number;
  defenseAndSettlementAmendatoryEndorsement?: boolean;
  defenseAndSettlementAmendatoryPercentage?: string;
  businessIncomeAndExtraExpenseSubLimit?: number;
  fundsTransferLiabilitySubLimit?: number;
  reputationalHarmSubLimit?: number;
  blanketAdditionalInsuredEndorsement?: boolean;
  contingentBusinessIncomeSecurityFailureEndorsement?: boolean;
  contingentBusinessIncomeSystemFailureItEndorsement?: boolean;
  serviceFraudAndCryptojackingSubLimit?: number;
  dedicatedBreachCostsEndorsementSubLimit?: number;
  enhancedBusinessAndCbi?: boolean;
  enhancedBusinessAndCbiWithSystemFailure?: boolean;
  exclusionOfCbi?: boolean;
  reverseSocialEngineeringEndorsement?: boolean;
  reverseSocialEngineeringLimit?: number;
  reverseSocialEngineeringDeductible?: number;
  backupFrequency?: string;
  businessContinuityMeasures?: string[];
  dynamicFields?: Record<
    string,
    { value?: string; values?: { value: string }[] }
  >;
}

export interface CalculateP100RateResponse {
  additionalBrokerFee: number;
  brokerFee: number;
  premium: number;
  totalPremium: number;
}

/** Mirrors fir-account's RateFirmographicsDto -- toRateRequest defaults missing values instead of omitting keys, except websites (must stay omitted, not []). */
export interface RateFirmographics {
  businessName: string;
  naicsCode: string;
  naicsDescription: string;
  revenue: number;
  numberOfEmployees: number;
  companyType: string;
  state: string;
  zipCode: string;
  entityType: string;
  yearEstablished: number;
  claimHistory: number;
  websites?: string[];
}

/** Every field here is genuinely optional (matches LegacySecurityQuestions on fir-account, which
 * has no @NotNull/@NotBlank at all) -- unlike the rest of RateRequest, this block itself and its
 * own fields are exempt from the "everything must be present" rule. */
export interface RateSecurityQuestions {
  questionEncryption?: boolean;
  questionLeadership?: boolean;
  questionCloud?: boolean;
  questionTraining?: boolean;
  backupFrequency?: string;
}

/** Shared with fir-account's RateCoverageOverlapDto -- fields both V5 and V6 rating read. */
export interface RateCoverageOverlap {
  extortionThreatsAndRansomPayment: boolean;
  ransomPaymentLimit?: number;
  isTriaExcluded: boolean;
  hardwareReplCostEndorsement: boolean;
  hardwareReplCostSubLimit: number;
  postBreachRemediationEndorsement: boolean;
  postBreachRemediationSubLimit: number;
  telecomsFraudEndorsement: boolean;
  telecomsFraudSubLimit: number;
  websiteMediaContentLiabilityEndorsement: boolean;
  websiteMediaContentLiabilitySubLimit: number;
  /** No companion *Endorsement boolean -- must be omitted, not defaulted to 0, when unselected. */
  serviceFraudAndCryptojackingSubLimit?: number;
  dedicatedBreachCostsEndorsementSubLimit?: number;
}

/** Mirrors fir-account's RateCoverageV6Dto -- the coverages shape POST /v1/rates uses for a V6-rated quote. */
export interface RateCoverageSelectionsV6 extends RateCoverageOverlap {
  websiteMediaContentLiabilityDeductible: number;
  blanketAdditionalInsuredEndorsement: boolean;
  contingentBusinessIncomeSecurityFailureEndorsement: boolean;
  contingentBusinessIncomeSystemFailureItEndorsement: boolean;
  /** Also self-gating -- no companion boolean, must omit rather than default to 0. */
  businessIncomeAndExtraExpenseSubLimit?: number;
  fundsTransferLiabilitySubLimit?: number;
  reputationalHarmSubLimit?: number;
  fundsTransferFraudCoverageLimit: number;
  socialEngineeringFraudCoverageLimit: number;
  fundsTransferFraudCoverageDeductible: number;
  socialEngineeringFraudCoverageDeductible: number;
  fundsTransferFraudAndSocialEngineeringEndorsement: boolean;
  fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement: boolean;
  fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement: boolean;
  invoiceManipulationEndorsement: boolean;
  invoiceManipulationSubLimit: number;
  invoiceManipulationDeductible: number;
  additionalDefenseExpensesLimitEndorsement: boolean;
  additionalDefenseExpensesLimit: number;
}

/** Mirrors fir-account's RateCoverageV5Dto -- the coverages shape POST /v1/rates uses for a V5-rated quote. */
export interface RateCoverageSelectionsV5 extends RateCoverageOverlap {
  businessIncomeCoverage: number;
  socialEngLimit: number;
  socialEngDeductible: number;
  computerFraudEndorsement: boolean;
  socialEngEndorsement: boolean;
  isVerifyingBankAccounts: boolean;
  isAuthenticatingFundTransferRequests: boolean;
  isPreventingUnauthorizedWireTransfers: boolean;
  enhancedBusinessAndCbi: boolean;
  enhancedBusinessAndCbiWithSystemFailure: boolean;
  exclusionOfCbi: boolean;
  reverseSocialEngineeringEndorsement: boolean;
  reverseSocialEngineeringLimit: number;
  reverseSocialEngineeringDeductible: number;
  /** V5 Enhanced-CBI-coverage-specific answer -- independent of securityQuestions.backupFrequency. */
  backupFrequency?: string;
  businessContinuityMeasures: string[];
}

export interface RateGlobals {
  effectiveDate: string;
  endDate: string;
  limit: number;
  deductible: number;
  retroactivePeriod: number;
  waitingPeriod: number;
}

/** POST /v1/rates payload. Every field is required except policyId (blank for a new-business
 * rate), accountId (fir-account resolves Boost's original_quote_created_date from it and skips
 * the lookup when it's absent -- the resolved date itself is server-side output, never sent by
 * the console), and securityQuestions (that block alone has no required sub-fields -- see
 * RateSecurityQuestions); toRateRequest defaults everything else to 0/''/[] instead of omitting
 * a key when the console page state doesn't have a value yet. */
export interface RateRequest {
  accountId?: string;
  policyId?: string;
  globals: RateGlobals;
  additionalBrokerFee: number;
  additionalInsureds: AdditionalInsureds[];
  dynamicFields: Record<
    string,
    { value?: string; values?: { value: string }[] }
  >;
  firmographics: RateFirmographics;
  securityQuestions?: RateSecurityQuestions;
  coverages: RateCoverageSelectionsV5 | RateCoverageSelectionsV6;
}

export interface UpdateSocialEngineeringPayload {
  accountId: string;
  isAuthenticatingFundTransferRequests: boolean;
  isPreventingUnauthorizedWireTransfers: boolean;
  isVerifyingBankAccounts: boolean;
}

export interface UpdateSocialEngineeringResponse {
  socialEngineering: boolean;
  reason?: SocialEngQuestions[];
}

export interface AddQuoteToCartRequest {
  agencyId?: string; // when called from Cowbell persona
  accountId: string;
  policyId?: string;
  limit: number;
  deductible: number;
  waitingPeriod: number;
  retroactivePeriod: number;
  premium: number;
  coverageExclusion?: string;
  businessIncomeCoverage: number;
  computerFraudEndorsement?: boolean;
  effectiveDate: string;
  endDate: string;
  socialEngEndorsement?: boolean;
  socialEngLimit?: number;
  socialEngDeductible?: number;
  ransomPaymentEndorsement?: boolean;
  ransomPaymentLimit?: number;
  hardwareReplCostEndorsement?: boolean;
  hardwareReplCostSubLimit?: number;
  telecomsFraudEndorsement?: boolean;
  telecomsFraudSubLimit?: number;
  postBreachRemediationEndorsement?: boolean;
  postBreachRemediationSubLimit?: number;
  websiteMediaContentLiabilityEndorsement?: boolean;
  websiteMediaContentLiabilitySubLimit?: number;
  websiteMediaContentLiabilityDeductible?: number;
  additionalInsureds?: AdditionalInsureds[];
  extortionThreatsLimitDetail?: string;
  pciFinesAndPenaltiesLimit?: number;
  regulatorDefenceAndPenaltiesLimit?: number;
  courtAttendanceCosts?: number;
}

export interface AddQuoteToCartResponse {
  id: string;
  message: string;
}

export interface BulkRequestQuotesRequest {
  accountId: string;
  agencyId?: string;
  agentEmail: string;
  agentFirstName: string;
  agentLastName: string;
  agentPhone: string;
  quoteIds: string[];
  teamIds?: string[];
}

export interface Prime100QuoteDetailsResponse extends P100QuoteDetailsDto {
  coverages: {
    name: Prime100Coverages;
    isSelected: boolean;
    limit: string | number;
    deductible: string | number;
    payload?: any;
  }[];
}

export type EditPrime100QuoteRequest = AddQuoteToCartRequest;

export type Prime100PolicyDetailsResponse = BasePolicyDto;

export interface RequestQuoteRequest extends AddQuoteToCartRequest {
  teamIds: string[];
}
